<template>
  <div
    class="pure-u-1 pure-g nav fixed-header"
    :class="{'logged-out' : !loggedIn}"
  >
    <client-only>
      <ImportantMessages
        v-if="importantMessages"
        :important-messages="importantMessages"
        class="head__notification"
        v-show="!showRegionSwitch && !isScrolled"
      />
      <div
        class="nav__waiting__indicator"
        v-if="!isReady"
      >
        <div
          class="skeleton"
        >
          <Skeleton class="skeleton__text" />
          <Skeleton class="skeleton__arrow" />
        </div>
      </div>
    </client-only>
    <header
      class="pure-u-1 pure-g nav"
      :class="{'hide-meta-menu': !loggedIn || availableCompanies <= 0 }"
    >
      <MetaHeader
        :is-ready="isReady && !companiesLoading"
        @reload-translations="handleReloadTranslations"
        @bell-clicked="notificationOverlay = !notificationOverlay"
        @login="login"
        @change-customer="handleCustomerChange"
      />
      <HeaderLoggedInSkeleton v-show="(!isReady && loggedIn) || companiesLoading" />
      <HeaderLoggedOutSkeleton v-show="!isReady && !loggedIn" />
      <div class="mobile-nav">
        <div
          class="nav-header-wrapper"
          :class="{'scrolled': isScrolled && isCategoryPage && currentCategoryType !== 'top'}"
        >
          <div class="nav-header">
            <div
              class="nav-close-button"
              v-if="isOpenedMobile"
              @click="toggleNavigation(false, 'mobile')"
            >
              <NavigationClose class="mobile-icon" />
            </div>
            <div
              class="nav-menu-button"
              v-if="!isOpenedMobile"
              @click="toggleNavigation(true, 'mobile')"
            >
              <NavigationMenu class="mobile-icon" />
            </div>
            <div class="nav-header-img">
              <nuxt-link
                :to="localePath('/')"
                @click="toggleNavigation(false, 'mobile')"
              >
                <img
                  :src="'/img/logo_fst.svg'"
                  :alt="$tv('Logo')"
                >
              </nuxt-link>
            </div>
            <div class="nav-header-icons">
              <client-only>
                <NotificationBell
                  @bell-clicked="notificationOverlay = !notificationOverlay"
                  :unread-notifications-length="unreadNotifications.length"
                  v-if="loggedIn"
                />
                <MountedTeleport
                  :to="'.modal-container'"
                >
                  <PopupBase
                    class="desktop-move-notifications"
                    :show="notificationOverlay"
                    @close-pop-up="notificationOverlay = false"
                  >
                    <div
                      class="h3"
                    >
                      {{ $tv('notifications', 'notifications') }}
                    </div>
                    <NotificationList
                      :notifications="unreadNotifications"
                      :notifications-hidden="notificationsHidden"
                    />
                  </PopupBase>
                </MountedTeleport>
                <nuxt-link
                  :to="localePath('/profile')"
                  @click="toggleNavigation(false, 'mobile')"
                  v-if="loggedIn"
                >
                  <SocialPerson class="mobile-icon user" />
                </nuxt-link>
                <a
                  v-if="!loggedIn"
                  href="#"
                  @click.prevent="login"
                >
                  <SocialPerson class="mobile-icon user" />
                </a>
                <nuxt-link
                  :to="localePath('/cart')"
                  v-if="loggedIn && hasCartViewPermission"
                  class="cart-mobile-icon"
                >
                  <ActionShoppingCart class="mobile-icon" />
                  <span
                    class="count"
                    v-text="count"
                  />
                </nuxt-link>
              </client-only>
              <a
                @click.prevent="openSearch()"
                href="#"
              ><ActionSearch class="mobile-icon search" /></a>
              <MountedTeleport :to="'.modal-container'">
                <PopupBase
                  :show="showSearchModal"
                  @close-pop-up="showSearchModal = false"
                >
                  <NewSearchOverlayMobile
                    class="mobileModal"
                    :focus="searchFocussed"
                    @search-blur="searchFocussed = false"
                    :search-term-result="searchTermResult"
                    :l2categories="primaryl2Categories"
                    :categories="l2Categories"
                    :search-term="searchTerm"
                    :is-inch-search="isInchSearch"
                    :is-any-search="isAnySearch"
                    @close-overlay="showSearchModal = false"
                    @search-term="getOverlay"
                    :is-loading="isLoading"
                    @openBuyBox="openBuyBox"
                    @openBuyBoxSku="openBuyBoxSku($event)"
                    @changeUnit="changeUnit"
                    @clickItem="clickItem"
                  />
                </PopupBase>
              </MountedTeleport>
            </div>
          </div>
        </div>
        <div
          class="mobile-menu-wrapper"
          :class="{'logged-out': !$auth.loggedIn}"
        >
          <MobileMenu
            :menu-points="mobileMenuSections"
            :open="isOpenedMobile"
            @reload-translations="handleReloadTranslations"
            @close-menu="toggleNavigation(false, 'mobile')"
          />
        </div>
      </div>
      <div
        class="pure-u-1 nav__main"
        :class="{'scrolled': isScrolled, 'mega-menu__open': menuOpen, 'no-navigation': !showNavigation, 'no-category-page': !isCategoryPage, 'category-page-top': isCategoryPage && currentCategoryType === 'top'}"
      >
        <div class="header-row header-row-container">
          <div class="nav__main__container nav__main__logo">
            <nuxt-link
              :to="homeLocalePath"
            >
              <img
                :src="'/img/logo_fst.svg'"
                alt="Freudenberg"
              >
            </nuxt-link>
          </div>
          <div class="nav__main__container nav__main__search pure-form pure-u-1-2">
            <ToolTip
              v-if="showCategoryToolTip"
              :show-tool-tip="showCategoryToolTip"
              tool-tip-position="bottom"
              arrow-position-vertically="top"
              arrow-position-horizontally="left"
              :hide-arrow="true"
              :id="'category-tooltip'"
              v-click-outside="handleCategoryTooltip"
            >
              <TreeView
                :data-items="treeDataItems"
                :expand-icons="true"
                @expandchange="onExpandChange"
                @itemclick="onItemClick"
              />
            </ToolTip>
            <SearchInputField
              :selected-category="selectedCategory"
              @search-triggered="toggleNavigation(false)"
              @searchTerm="getOverlay"
              @openOverlay="handleOpenOverlay"
              @close-overlay="showOverlay = false"
              :is-loading="isLoading"
              :is-inch-search="isInchSearch"
              :direct-link="onlyResultDirectLink"
              :overlay-visible="showOverlay"
              @changeUnit="changeUnit"
              @open-category-tooltip="handleCategoryTooltip"
              :search-term-result="searchTermResult"
            />
          </div>
          <div
            class="nav__main__container nav__main__overlay"
          >
            <NewSearchOverlayDesktop
              v-show="showOverlay"
              :search-term-result="searchTermResult"
              :l2categories="primaryl2Categories"
              :categories="l2Categories"
              :search-term="searchTerm"
              :search-filter="selectedCategory.searchFilter"
              :is-inch-search="isInchSearch"
              :is-any-search="isAnySearch"
              :product-availabilities="productAvailabilities"
              :suggestion-availabilities="suggestionAvailabilities"
              :loaded-product-availability-skus="loadedProductAvailabilitySkus"
              :loaded-suggestion-availability-skus="loadedSuggestionAvailabilitySkus"
              @close-overlay="showOverlay = false"
              @openBuyBox="openBuyBox"
              @openBuyBoxSku="openBuyBoxSku($event)"
              @clickItem="clickItem"
            />
          </div>
          <div class="nav__main__container nav__main__logos">
            <div v-show="$auth && $auth.loggedIn">
              <img
                :src="BASE_URL + '/img/logo_sealing_tech_with_easy_eservices.svg'"
                :alt="$tv('sealingTechnologies','nagivation')"
                class="svg__w-easy"
              >
            </div>
            <div v-show="!$auth || !$auth.loggedIn">
              <img
                :src="BASE_URL + '/img/logo-innovating-together.svg'"
                :alt="$tv('sealingTechnologies','nagivation')"
                class="svg__w-easy"
              >
            </div>
          </div>
          <client-only>
            <div
              class="nav__main__container nav__main__business-unit nav__main__shop"
              v-show="$auth && $auth.loggedIn"
            >
              <div>
                <nuxt-link
                  :to="localePath('/cart')"
                  v-if="hasCartViewPermission"
                  :title="$tv('services.shoppingCartIconTooltip', 'header')"
                  class="nav__main__shop__cart__link"
                >
                  <div
                    class="nav__main__shop__icon"
                  >
                    <span
                      v-if="count || count === 0"
                      class="nav__main__shop__icon__bubble"
                      :class="{pulse: pulseCart}"
                      v-text="count"
                    />
                    <span
                      v-else
                      class="nav__main__shop__icon__bubble k-i-loading loading-indicator loading-indicator--small"
                    />
                    <ActionShoppingCart
                      :title="$tv('services.shoppingCartIconTooltip', 'header')"
                      class="mobile-icon cart"
                    />
                  </div>
                </nuxt-link>
              </div>
            </div>
          </client-only>
        </div>
        <div
          class="header-row"
          :class="{'scrolled': isScrolled}"
        >
          <div class="nav__main__container nav__main__categories">
            <a
              href="#"
              class="hover-effect__underlined"
              :class="{'mega-menu_cat': menuCatOpenHover}"
              @mouseover="toggleNavigation(true, 'catHover')"
              @mouseleave="toggleNavigation(false, 'catHover')"
            >
              <span>{{ $tv('products', 'menu') }}</span>
              <span class="k-icon k-i-caret-alt-down k-button-icon k-i-arrow-chevron-down" />
            </a>
            <a
              href="#"
              class="hover-effect__underlined"
              :class="{'mega-menu_sel': menuSelectorsOpenHover}"
              @mouseover="toggleNavigation(true, 'selHover')"
              @mouseleave="toggleNavigation(false, 'selHover')"
            >
              <span>{{ $tv('selectors', 'menu') }}</span>
              <span class="k-icon k-i-caret-alt-down k-button-icon k-i-arrow-chevron-down" />
            </a>
          </div>
          <div class="nav__main__service_menu">
            <div
              v-for="(service, i) in services"
              :key="'service'+i"
            >
              <a
                v-if="!service.localRoute"
                class="hover-effect__underlined"
                :href="service.externalUrl"
                :target="service.externalUrl ? '_blank' : '_self'"
              >{{ service.label }}</a>
              <nuxt-link
                v-else
                class="hover-effect__underlined"
                :to="localePath(service.localRoute)"
              >
                {{ service.label }}
              </nuxt-link>
            </div>
          </div>
        </div>
        <PageNavigation
          :class="{'scrolled': !showNavigation}"
          :list-links="listLinks"
        />
      </div>
      <div
        v-show="menuOpen"
        class="megamenu"
        @mouseover="clearTimeout(this.resetHover)"
        @mouseleave="toggleNavigation(false)"
      >
        <MegaMenu
          v-show="menuCatOpenHover"
          :sections="menuCategories"
          @close-menu="toggleNavigation(false)"
        />
        <MegaMenu
          v-show="menuSelectorsOpenHover"
          :sections="menuSelectors"
          @close-menu="toggleNavigation(false)"
        />
      </div>
      <client-only>
        <MountedTeleport :to="'.modal-container'">
          <PopupBase
            :show="openCartModal"
            @close-pop-up="openCartModal = false"
            :css-class="buyboxClass"
            :close-on-outside-click="true"
          >
            <ProductBuyBox
              v-if="product"
              :is-default-customer="isDefaultCustomer"
              :product="product"
              :product-data="product"
              class="buy-box-in-popup"
              @productSuccessfullyAdded="onProductSuccessfullyAdded"
              @reloadProduct="reloadProduct"
              @close-pop-up="openCartModal = false"
            />
          </PopupBase>
        </MountedTeleport>
      </client-only>
    </header>
  </div>
</template>

<script>
import MetaHeader from '~/components/header/MetaHeader'
import PopupBase from '~/components/popup/PopupBase.vue'
import NewSearchOverlayMobile from '~/components/search/NewSearchOverlayMobile.vue'
import SearchInputField from '../search/SearchInputField'
import PageNavigation from './PageNavigation'
import {storeToRefs} from 'pinia'
import NotificationBell from '~/components/notifications/NotificationBell.vue'
import NotificationList from '~/components/notifications/NotificationList.vue'
import NewSearchOverlayDesktop from '~/components/search/NewSearchOverlayDesktop.vue'
import {useProductService} from '@/composables/product_service'
import {useCustomerService} from '@/composables/customer_service'
import {usePandaService} from '~/composables/panda_service'
import {useRightsHelper} from '~/composables/rights_helper'
import ProductBuyBox from '~/components/product-detail/ProductBuyBox.vue'
import {SEARCH, USER_RIGHTS_MAPPING, NON_ATTRIBUTE_FILTERS_NEW, PROPERTY_SCOPE_NAMES, CUSTOMER_SETTING_KEYS, REGION_KEYS} from '~/assets/js/constants'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import NavigationMenu from '~/assets/icons/navigation_menu.svg?component'
import SocialPerson from '~/assets/icons/social_person.svg?component'
import ActionShoppingCart from '~/assets/icons/action_shopping_cart.svg?component'
import ActionSearch from '~/assets/icons/action_search.svg?component'
import {useDefaultCustomer} from '~/composables/default_customer'
import {INCH_FILTER, SHOW_INCH_VALUES_FILTER} from '~/assets/js/filters'
import {useReloadProduct} from '~/composables/reload_product'
import {useCategoryDesignService} from '@/composables/category_design_service'
import {Skeleton} from '@progress/kendo-vue-indicators'
import {TreeView, processTreeViewItems} from '@progress/kendo-vue-treeview'
import {useUserStore} from '@/store/user'
import {useNotificationStore} from '@/store/notification'
import {useCartStore} from '@/store/cart'
import {useCustomerSettingsStore} from '@/store/customer-settings'
import MountedTeleport from '~/components/MountedTeleport'
import ImportantMessages from '~/components/header/ImportantMessages'
import HeaderLoggedInSkeleton from '../skeletons/HeaderLoggedInSkeleton.vue'
import HeaderLoggedOutSkeleton from '../skeletons/HeaderLoggedOutSkeleton.vue'
import MegaMenu from '~/components/header/MegaMenu.vue'
import MobileMenu from '~/components/navigation/MobileMenu.vue'
import usePim from '~/composables/pim_json_loader'
import useI18nExtended from '~/composables/usei18nExtended'

export default defineComponent({
  components: {
    ProductBuyBox,
    MetaHeader,
    PageNavigation,
    PopupBase,
    SearchInputField,
    NotificationList,
    NotificationBell,
    NewSearchOverlayDesktop,
    NavigationClose,
    NavigationMenu,
    SocialPerson,
    ActionShoppingCart,
    ActionSearch,
    Skeleton,
    TreeView,
    MountedTeleport,
    ImportantMessages,
    HeaderLoggedInSkeleton,
    HeaderLoggedOutSkeleton,
    MegaMenu,
    MobileMenu,
    NewSearchOverlayMobile
  },
  setup() {
    const nuxtApp = useNuxtApp()
    const i18n = useI18nExtended()
    const categoriesJson = ref([])
    const categoriesFlatJson = ref([])
    const currentCategoryType = ref('')
    const lastScrollPosition = ref(0)
    const isScrolled = ref(false)

    const {getFilteredCustomerList} = useCustomerService()
    const {getCategoriesByTechnicalNames} = useCategoryDesignService()

    const {settings, loggedIn, loadedCustomerUuid, showInchValues, excludedBrands, name, email, selectedCustomer, userUuid, includedBrandCodes} = storeToRefs(useUserStore())
    const {unreadNotifications} = storeToRefs(useNotificationStore())
    const {cartCountComputed} = storeToRefs(useCartStore())
    const {currentCustomerSettings} = storeToRefs(useCustomerSettingsStore())

    const {updateSettings, setAvailableCompanies, setCompanies, setShowInchValues} = useUserStore()
    const {availableCompanies, companiesLoading} = storeToRefs(useUserStore())

    const {getProductsBySearchTermNew, getCustomerSuggestions, getProductBySku, getProductBySkuNew} = useProductService()

    const config = useRuntimeConfig().public

    const {isDefaultCustomer} = useDefaultCustomer()

    const product = ref(null)
    const {reloadProduct, reloadProductsInProgress} = useReloadProduct(product, i18n.locale, nuxtApp.$globalization)

    const {postPandaAvailabilitiesBulk} = usePandaService()
    const {userHasRight} = useRightsHelper()

    return {
      getFilteredCustomerList,
      getCategoriesByTechnicalNames,
      userSettings: settings,
      loggedIn,
      currentCustomerUuid: loadedCustomerUuid,
      showInchValues,
      unreadNotifications,
      count: cartCountComputed,
      currentCustomerSettings,
      updateSettings,
      setAvailableCompanies,
      setCompanies,
      setShowInchValues,
      getProductsBySearchTermNew,
      getProductBySku,
      getProductBySkuNew,
      getCustomerSuggestions,
      BASE_URL: config.BASE_URL,
      availableCompaniesStore: availableCompanies,
      companiesLoading,
      excludedBrands,
      userEmail: email,
      userName: name,
      nuxtApp,
      includedBrandCodes,
      userUuid,
      selectedCustomer,
      categoriesJson,
      categoriesFlatJson,
      isDefaultCustomer,
      product,
      reloadProduct,
      reloadProductsInProgress,
      postPandaAvailabilitiesBulk,
      userHasRight,
      lastScrollPosition,
      isScrolled,
      currentCategoryType
    }
  },
  data() {
    return {
      searchTerm: '',
      showSearchModal: false,
      notificationOverlay: false,
      searchFocussed: false,
      notificationsHidden: {},
      searchTermResult: {},
      primaryl2Categories: [],
      l2Categories: [],
      showOverlay: false,
      isLoading: false,
      openCartModal: false,
      pulseCart: false,
      isReady: false,
      searchTime: null,
      searchDebouncer: null,
      selectedCategory: {text: this.$tv('all', 'search'), id: 'all'},
      showCategoryToolTip: false,
      check: [],
      expand: {
        ids: ['Item2'],
        idField: 'text'
      },
      threshold: 90,
      thresholdUp: 90,
      select: [''],
      tooltipTimer: null,
      menuCatOpenHover: false,
      menuSelectorsOpenHover: false,
      resetHover: null,
      isAnySearch: false,
      socialIntentsLoadedOnce: false,
      isOpenedMobile: false,
      productAvailabilities: [],
      suggestionAvailabilities: [],
      loadedProductAvailabilitySkus: [],
      loadedSuggestionAvailabilitySkus: [],
      availabilityLoadingInProgress: false,
      lastAvailabilitySearchTime: null
    }
  },
  props: {
    importantMessages: {
      type: Array,
      default() {
        return []
      }
    }
  },
  fetchOnServer: false,
  async mounted() {
    window.addEventListener('scroll', this.handleScroll)
    useNuxtApp().$eventbus.on('category-type-change', this.onCategoryTypeChange)
    const region = this.$globalization.getRegion().toLocaleLowerCase()
    this.categoriesJson = await usePim(`v1/${region}/${this.$i18n.locale}/categories_menu.json`)
    this.categoriesFlatJson = await usePim(`v1/${region}/${this.$i18n.locale}/categories_flat.json`)
    this.isReady = true
  },
  methods: {
    onCategoryTypeChange(categoryType) {
      this.currentCategoryType = categoryType
    },
    handleScroll() {
      const currentScrollY = window.scrollY
      const overThreshold = currentScrollY > this.threshold

      if (this.isCategoryPage && this.currentCategoryType !== 'top') {
        if (currentScrollY > this.lastScrollPosition) {
        // scrolling down
          this.isScrolled = overThreshold && !this.menuOpen
        } else if (currentScrollY < this.lastScrollPosition) {
          // scrolling up
          const overThreshold = currentScrollY > this.thresholdUp
          this.isScrolled = overThreshold && !this.menuOpen
        }
      }

      if (!this.isCategoryPage || this.currentCategoryType === 'top') {
        this.isScrolled = currentScrollY > this.lastScrollPosition && !this.menuOpen
      }
      this.lastScrollPosition = currentScrollY
    },
    handleReloadTranslations() {
      this.$emit('reload-translations')
    },
    handleCustomerChange() {
      this.$emit('change-customer')
    },
    clearTimeout(timeout) {
      clearTimeout(timeout)
    },
    toggleNavigation(state, type = null) {
      if (type === 'catHover') {
        if (state) {
          this.menuCatOpenHover = state === null ? !this.menuCatOpenHover : state
          clearTimeout(this.resetHover)
        } else {
          this.resetHover = setTimeout(() => {this.menuCatOpenHover = state === null ? !this.menuCatOpenHover : state}, 200)
        }
        this.menuSelectorsOpenHover = state === null ? false : false
        this.isOpenedMobile = state === null ? false : false
      } else if (type === 'selHover') {
        if (state) {
          this.menuSelectorsOpenHover = state === null ? !this.menuSelectorsOpenHover : state
          clearTimeout(this.resetHover)
        } else {
          this.resetHover = setTimeout(() => {this.menuSelectorsOpenHover = state === null ? !this.menuSelectorsOpenHover : state}, 200)
        }
        this.menuCatOpenHover = state === null ? false : false
        this.isOpenedMobile = state === null ? false : false
      } else if (type === 'mobile') {
        this.isOpenedMobile = state === null ? !this.isOpenedMobile : state
        this.menuCatOpenHover = state === null ? false : false
        this.menuSelectorsOpenHover = state === null ? false : false
      } else {
        this.menuCatOpenHover = state === null ? false : false
        this.menuSelectorsOpenHover = state === null ? false : false
        this.isOpenedMobile = state === null ? false : false
      }
    },
    openSearch() {
      this.showSearchModal = true
      this.searchFocussed = true
    },
    onItemClick(event) {
      this.select = [event.itemHierarchicalIndex]
      this.selectedCategory = event.item
      this.showCategoryToolTip = false
      if (this.searchTerm) {
        this.getOverlay(this.searchTerm)
      }
    },
    onExpandChange(event) {
      let ids = this.expand.ids.slice()
      const index = ids.indexOf(event.item.text)
      index === -1 ? ids.push(event.item.text) : ids.splice(index, 1)
      this.expand = {
        ids,
        idField: 'text'
      }
    },
    handleOpenOverlay(value) {
      this.showOverlay = true
      if (Object.keys(this.searchTermResult ?? {}).length === 0 && value) {
        this.getOverlay(value)
      }
    },
    async getOverlay(value) {
      if (this.searchDebouncer) {
        clearTimeout(this.searchDebouncer)
      }

      this.searchDebouncer = setTimeout(async () => {
        this.searchTermResult = {}
        this.primaryl2Categories = []
        this.isLoading = true
        this.productAvailabilities = []
        this.suggestionAvailabilities = []
        this.loadedProductAvailabilitySkus = []
        this.loadedSuggestionAvailabilitySkus = []
        this.lastAvailabilitySearchTime = null
        if (value.length >= 1) {
          this.searchTerm = value
          this.showOverlay = true
          // use pagination options to get only the first X items from API
          const options = {
            pagination: {
              take: this.$config.public.topProductResults,
              skip: 0
            },
            sort: []
          }
          let time = Date.now()
          this.searchTime = time

          let designFilter = null
          if (this.selectedCategory.id !== 'all') {
            designFilter = this.selectedCategory.id
          }
          let res = null
          let customerRes = null
          const promises = []
          let endTimeSuggestions = null
          let endTimeProducts = null
          if (this.$auth.loggedIn) {
            let suggestFunction = async() => {customerRes = await this.getCustomerSuggestions(this.searchTerm, this.$globalization.getRegion(), this.$i18n.locale); endTimeSuggestions = Date.now()}
            promises.push(await suggestFunction())
          }
          let productFunction = async() => {res = await this.getProductsBySearchTermNew(this.searchTerm, [NON_ATTRIBUTE_FILTERS_NEW.PRIMARYL2CATEGORY], this.$globalization.getRegion(), this.$i18n.locale, options, null, this.isInchSearch ? [INCH_FILTER.customQuery, SHOW_INCH_VALUES_FILTER.customQuery] : null, PROPERTY_SCOPE_NAMES.SEARCH_OVERLAY_NEW, [], designFilter); endTimeProducts = Date.now()}
          promises.push(await productFunction())
          await Promise.all(promises)
          res = {...res, customerSuggestions: customerRes?.searchResults}
          if (this.searchTime !== time) {
            // discard results if they're not for most recent search
            return
          }
          this.searchTermResult = res
          this.isAnySearch = res?.isAnySearch ?? false
          this.$gtm.trackEvent({
            event: 'search',
            category: 'product_search',
            action: 'standard_search',
            ecommerce: {
              'search_term': this.searchTerm,
              'search_count': this.searchTermResult?.count}
          })
          this.$searchInsights?.trackEvent({
            name: 'Search', properties: {
              searchServiceName: this.searchTermResult?.azureSearchTelemetry?.searchServiceName,
              searchId: this.searchTermResult?.azureSearchTelemetry?.searchId,
              indexName: this.searchTermResult?.azureSearchTelemetry?.indexName,
              suggestIndexName: customerRes?.azureSearchTelemetry?.indexName,
              searchTerm: this.searchTerm,
              searchTermAzure: this.searchTermResult?.azureSearchTelemetry?.searchTermAzure,
              resultCount: this.searchTermResult?.count,
              suggestResultCount: customerRes?.searchResults?.length,
              anySearch: this.searchTermResult?.isAnySearch,
              searchDurationInMs: endTimeProducts - this.searchTime,
              azureSearchDurationInMs: this.searchTermResult?.azureSearchTelemetry?.searchDurationInMs,
              suggestSearchDurationInMs: endTimeSuggestions !== null ? endTimeSuggestions - this.searchTime : null,
              suggestAzureSearchDurationInMs: customerRes?.azureSearchTelemetry?.searchDurationInMs,
              brands: this.includedBrandCodes.join(','),
              customer: this.selectedCustomer?.customerNo,
              salesArea: `${this.selectedCustomer?.salesOrg}-${this.selectedCustomer?.salesChannel}-${this.selectedCustomer?.distribution}`,
              user: this.$auth.loggedIn ? this.userUuid : null
            }
          })
          if (this.searchTermResult) {
            this.primaryl2Categories = this.searchTermResult.facets?.primaryL2Category?.slice(0, SEARCH.MAX_FACET_DESIGNS).map(d => ({categoryName: d.value, hitCount: d.count})) || []
            this.l2Categories = this.categoriesFlatJson.filter(c => this.primaryl2Categories?.some(p => p.categoryName === c.technicalName))
            this.loadSearchAvailabilities()
          }
        }
        else {
          this.showOverlay = false
        }
        this.isLoading = false
      }, 300)
    },
    async loadSearchAvailabilities() {
      if (!this.userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)) {
        return
      }

      if (!this.showOverlay) {
        return
      }

      if (this.availabilityLoadingInProgress && this.lastAvailabilitySearchTime === this.searchTime) {
        return
      }

      if (this.lastAvailabilitySearchTime === this.searchTime &&
          (this.loadedProductAvailabilitySkus.length > 0 || this.loadedSuggestionAvailabilitySkus.length > 0)) {
        return
      }

      this.availabilityLoadingInProgress = true
      this.lastAvailabilitySearchTime = this.searchTime

      const nowUtcIsoString = new Date()
      const promises = []

      const products = this.searchTermResult?.searchResults || []
      if (products.length > 0) {
        const productSkus = products.map(p => p?.sku)
        const productsToRequest = products.map(p => ({
          sku: p?.sku,
          availabilityDate: nowUtcIsoString,
          quantity: 1,
          defaultPlant: false
        }))
        promises.push(
          this.postPandaAvailabilitiesBulk(productsToRequest).then(response => {
            if (response) {
              this.productAvailabilities = response
              this.loadedProductAvailabilitySkus = productSkus
            }
          })
        )
      } else {
        this.productAvailabilities = []
        this.loadedProductAvailabilitySkus = []
      }

      const suggestions = this.searchTermResult?.customerSuggestions || []
      if (suggestions.length > 0) {
        const suggestionSkus = suggestions.map(s => s?.Sku).slice(0, 4)
        const suggestionsToRequest = suggestions.map(s => ({
          sku: s?.Sku,
          availabilityDate: nowUtcIsoString,
          quantity: 1,
          defaultPlant: false
        })).slice(0, 4)
        promises.push(
          this.postPandaAvailabilitiesBulk(suggestionsToRequest).then(response => {
            if (response) {
              this.suggestionAvailabilities = response
              this.loadedSuggestionAvailabilitySkus = suggestionSkus
            }
          })
        )
      } else {
        this.suggestionAvailabilities = []
        this.loadedSuggestionAvailabilitySkus = []
      }

      try {
        await Promise.all(promises)
      } finally {
        this.availabilityLoadingInProgress = false
      }
    },
    login() {
      navigateTo({path: '/auth/login'})
    },
    openBuyBox(product) {
      this.product = product
      this.openCartModal = true
    },
    async openBuyBoxSku(sku) {
      this.product = await this.getProductBySkuNew(sku, this.$globalization.getRegion(), this.$i18n.locale, this.showInchValues, null, PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW)
      if (this.product) {
        this.openCartModal = true
        this.showOverlay = false
      } else {
        this.$toast.error(this.$tv('productNotAccessible', 'product'))
      }
    },
    onProductSuccessfullyAdded() {
      this.openCartModal = false
    },
    onClickOutside() {
      if (this.showOverlay) {
        this.showOverlay = false
      }
    },
    changeUnit(val) {
      this.searchTerm = val
      if (this.searchTerm) {
        this.getOverlay(this.searchTerm)
      }
      this.setShowInchValues(!this.showInchValues)
    },
    clickItem(sku, rank) {
      this.$searchInsights?.trackEvent({
        name: 'Click', properties: {
          searchServiceName: this.searchTermResult?.azureSearchTelemetry?.searchServiceName,
          searchId: this.searchTermResult?.azureSearchTelemetry?.searchId,
          clickedDocId: sku,
          sku: sku,
          rank: rank
        }
      })
    },
    resetSearch() {
      this.searchTermResult = {}
      this.primaryl2categories = []
      this.l2Categories = []
      this.searchTerm = ''
      this.isAnySearch = false
    },
    handleCategoryTooltip() {
      clearTimeout(this.tooltipTimer)
      this.tooltipTimer = setTimeout(() => this.showCategoryToolTip = !this.showCategoryToolTip, 100)
    }
  },
  watch: {
    'socialIntentsChatId': {
      immediate: true,
      handler(val) {
        if (this.socialIntentsLoadedOnce) {
          if (!this.hasShowSocialIntentsChat) {
            this.$hideTabSocialIntents()
          } else {
            this.$showTabSocialIntents()
          }
        }
        if (val?.length > 0 && this.loggedIn && this.hasShowSocialIntentsChat && !this.socialIntentsLoadedOnce) {
          this.$loadSocialIntents(val)
          this.socialIntentsLoadedOnce = true
        }
      }
    },
    'items.length': {
      handler() {
        this.pulseCart = true
        setTimeout(() => this.pulseCart = false, 1200)
      }
    },
    '$i18n.locale': {
      async handler() {
        if (this.selectedCategory?.id !== 'all') {
          this.selectedCategory = {text: this.$tv(this.selectedCategory.id, 'categories'), id: this.selectedCategory.id}
        } else {
          this.selectedCategory = {text: this.$tv(this.selectedCategory.id, 'search'), id: this.selectedCategory.id}
        }
        this.categoriesJson = await usePim(`v1/${this.$globalization.getRegion().toLocaleLowerCase()}/${this.$i18n.locale}/categories_menu.json`)
        this.categoriesFlatJson = await usePim(`v1/${this.$globalization.getRegion().toLocaleLowerCase()}/${this.$i18n.locale}/categories_flat.json`)
      }
    },
    currentCustomerUuid: {
      immediate: true,
      handler() {
        this.resetSearch()
      }
    },
    '$route.path': {
      handler() {
        this.showCategoryToolTip = false
      }
    }
  },
  computed: {
    menuOpen() {
      return this.menuCatOpenHover || this.menuSelectorsOpenHover
    },
    mobileMenuSections() {
      let sections = []

      sections.push({
        label: this.$tv('products', 'menu'),
        localRoute: '',
        Subsections: this.menuCategories
      })

      if (this.menuSelectors.length > 0) {
        sections.push(
          this.menuSelectors[0]
        )
      }

      sections = sections.concat(this.services)

      if (this.hasRight(USER_RIGHTS_MAPPING.ORDER_HISTORY_VIEW) || this.hasRight(USER_RIGHTS_MAPPING.QUOTE_HISTORY_VIEW)) {
        sections.push({
          label: this.$tv('order-history', 'product'),
          localRoute: 'orderHistory'
        })
      }

      return sections
    },
    availableCompanies() {
      return this.availableCompaniesStore?.length
    },
    socialIntentsChatId() {
      const showSocialIntentsChatId = this.currentCustomerSettings?.find(s => s.translationKey === CUSTOMER_SETTING_KEYS.SocialIntentsChatId)
      return showSocialIntentsChatId?.customerSettingValue
    },
    hasShowSocialIntentsChat() {
      // eslint-disable-next-line eqeqeq
      return this.socialIntentsChatId != null && this.socialIntentsChatId !== ''
    },
    buyboxClass() {
      return 'popup__buy-box-new'
    },
    allowedRegions() {
      return (useRuntimeConfig().public.CCRS_ALLOWED_REGIONS || 'EU').split(',').some(item => item.toLowerCase() === this.$globalization.getRegionFromURL()?.toLowerCase())
    },
    services() {
      let services = [
        {
          label: this.$tv('services.DistributorSearch', 'header'),
          localRoute: '/distributor-search'
        }
      ]
      if (this.allowedRegions) {
        services.push({
          label: this.$tv('title', 'ccrs'),
          localRoute: 'cross-reference'
        })
      }
      services.push({
        label: this.$tv('title', 'chemicalResistanceGuide'),
        localRoute: 'chemical-resistance-guide'
      })
      if (this.loggedIn && this.currentCustomerSettings?.some(s => s.translationKey === CUSTOMER_SETTING_KEYS.XpressAllowed && (s.customerSettingValue === '1'))) {
        services.push({
          label: this.$tv('services.xpressMenuLink', 'header'),
          localRoute: 'xpress'
        })
      }
      return services
    },
    menuSelectors() {
      let selectors = []
      const config = useRuntimeConfig().public
      selectors.push({
        label: this.$tv('services.ORingConfigurator', 'header'),
        localRoute: '/oring-calculator'
      })
      if (this.$globalization.getRegion() !== REGION_KEYS.NA || config.HYDRAS_LINK_DISABLED_NA !== '1') {
        selectors.push({
          label: this.$tv('services.hydraulicSelector', 'header'),
          localRoute: '/hydraulic-selector'
        })
      }
      if (this.$globalization.getRegion() !== REGION_KEYS.NA || config.ACCUS_LINK_DISABLED_NA !== '1') {
        selectors.push({
          label: this.$tv('services.accumulatorSelector', 'header'),
          localRoute: '/accumulator-selector'
        })
      }
      if (config.SIMMERINGSELECTOR_URL) {
        selectors.push({
          label: this.$tv('services.SimmerringSelector', 'header'),
          localRoute: '/simmerring-selector'
        })
      }

      let services = [{
        label: this.$tv('selectorsMenuLabel', 'header'),
        localRoute: '',
        Subsections: selectors
      }]
      return services
    },
    hasCartViewPermission() {
      return this.hasRight(this.USER_RIGHTS_MAPPING.CART_VIEW)
    },
    listLinks() {
      const listLinks = []

      if (this.hasRight(USER_RIGHTS_MAPPING.ORDER_HISTORY_VIEW) || this.hasRight(USER_RIGHTS_MAPPING.QUOTE_HISTORY_VIEW)) {
        listLinks.push({label: this.$tv('order-history', 'product'), url: 'orderHistory'})
      }
      return listLinks
    },
    isInchSearch() {
      return this.showInchValues
    },
    treeDataItems() {
      return processTreeViewItems(this.categoriesTree, {
        select: this.select,
        check: this.check,
        expand: this.expand
      })
    },
    filteredCategories() {
      const hasBrandInRegion = brands => brands.some(b => !this.excludedBrands.includes(b.substring(0, 1).toLowerCase()))
      return this.categoriesJson
        .map(c => ({
          ...c,
          Subcategories: c.subcategories?.filter(sc => hasBrandInRegion(sc.brands))
        }))
        .filter(c => hasBrandInRegion(c.brands))
    },
    menuCategories() {
      return this.categoriesJson?.map(c => ({
        label: c.shortName || this.$tv(c.technicalName, 'categories'),
        localRoute: c.url,
        Subsections: c.subcategories?.map(sc => ({
          label: sc.shortName || this.$tv(sc.technicalName, 'categories'),
          localRoute: sc.url,
          brands: sc.brands.map(b => ({Code: b}))
        })),
        brands: c.brands.map(b => ({Code: b}))
      }))
    },
    categoriesTree() {
      const categoriesTree = [{
        text: this.$tv('all_long', 'search'),
        id: 'all',
        items: []
      }]

      this.filteredCategories.forEach(c => {
        const category = {
          text: c.shortName || this.$tv(c.technicalName, 'categories'),
          id: c.technicalName,
          isTopCategory: true,
          searchFilter: c.technicalName,
          items: c.Subcategories.map(sc => ({
            text: sc.shortName || this.$tv(sc.technicalName, 'categories'),
            id: sc.technicalName,
            isTopCategory: false,
            searchFilter: sc.Subcategories?.map(ssc => ssc.technicalName),
            items: []
          }))
        }
        categoriesTree.push(category)
      })

      return categoriesTree
    },
    onlyResultDirectLink() {
      const regularResults = this.searchTermResult?.searchResults?.length || 0
      const customerSuggestions = this.searchTermResult?.customerSuggestions?.length || 0

      if (customerSuggestions === 1 && regularResults === 0) {
        const suggestion = this.searchTermResult?.customerSuggestions[0]
        return this.localePath(`/products/${suggestion.Sku}`)
      }

      if (regularResults === 1) {
        const product = this.searchTermResult?.searchResults[0]
        return this.localePath(`/products/${product.sku}`)
      }
      return null
    },
    showNavigation() {
      const isHomePage = this.$route.path === this.localePath('/') || this.$route.path === '/'
      return (this.loggedIn || !isHomePage) && !this.isScrolled && !this.menuOpen
    },

    homeLocalePath() {
      return this.nuxtApp.$globalization.localePath('/', this.$i18n.locale, this.nuxtApp.$globalization.getRegion())
    },
    isCategoryPage() {
      const categoryBasePath = this.localePath('/categories')
      return this.$route.path.startsWith(categoryBasePath)
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    useNuxtApp().$eventbus.off('category-type-change')
  }
})
</script>

<style lang="scss">
.nav__main__shop__icon {
  .k-i-loading {
    font-size: .7rem !important;
  }
}

.nav__main__logo {
  max-width: 18%;
  width: 100%;
  min-width: 11.25rem;

  svg,
  img {
    width: 100%;
    height: auto;
  }
}

.nav {
  &__main__shop__cart__link {
    display: flex;
  }

  .pageNavigation {
    width: calc(100% + 2 * 2rem);
    display: flex;
    justify-content: space-between;
    padding: 0;
    border-top: .0625rem solid $color__fst__gray__030;
    height: 2.5rem;

    &.scrolled {
      opacity: 0;
      display: none;
    }
  }
}

.megamenu {
  background-color: $color__white;
  padding: 1.25rem 3.75rem 1.25rem 3rem;
  border-bottom: .125rem solid $color__fst__light__gray;

  @include breakpoint-down($md) {
    display: none;
  }
}

.mobile-menu-wrapper {
  display: none;

  &:has(.mobile-menu.open) {
    display: block;
  }
}

.fixed-header {
  z-index: 12;
  position: fixed;
  top: 0;
  max-width: 80rem;
  background-color: $color__white;
}

.nav__waiting__indicator {
  .skeleton {
    width: 100%;
    margin: .25rem 1rem;
    display: flex;
    align-items: center;

    .skeleton__arrow {
      height: .25rem;
      width: 1rem;
      margin-right: 0;
    }

    .skeleton__text {
      height: 1rem;
      flex-grow: 1;
      margin-right: 1rem;
    }
  }
}

.header-row {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.header-row:nth-child(2) {
  align-items: center;
  justify-content: flex-start;

  &.scrolled {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.5s ease;
  }
}

.nav__main__categories {
  display: flex;
  flex-wrap: nowrap;

  a {
    font-weight: $base-text-font-weight-semi-bold;
    color: $color__fst__primary;
    text-decoration: none;
    margin-right: 2rem;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;

    .k-icon {
      margin-left: .25rem;
    }

    &.mega-menu_cat::after,
    &.mega-menu_sel::after {
      position: absolute;
      content: '';
      bottom: 0;
      width: 100%;
      right: 0;
      display: inline-block;
      height: .125rem;
      background-color: $color__primary;
    }
  }
}

.nav__main__container {
  .k-button {
    min-width: 11.6rem;
  }

  > div {
    min-height: 2.5rem;
    display: flex;
    flex-direction: row;
    place-content: center center;
  }

  &.nav__main__shop > div {
    min-height: 2.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;

    > a {
      position: relative;
      top: .1rem;
    }
  }

  &.nav__main__logos {
    > div {
      min-height: 2.75rem;
      display: flex;
      flex-direction: column;
      align-content: center;
    }

    @include breakpoint-down($lg) {
      display: none;
    }

    img {
      max-width: 15rem;
    }

    .svg__w-easy {
      height: 2.125rem;
    }
  }
}

@include breakpoint-up($md) {
  .desktop-move-notifications .popup__wrapper {
    max-width: 30rem;
    left: 23.4rem;
    position: relative;
    top: 1.2rem;
  }

  .nav.nav .nav__waiting__indicator {
    display: none;
  }
}

@include breakpoint-down($md) {
  .nav.nav .nav__waiting__indicator {
    min-height: 2.188rem;
    height: 2.188rem;
  }
}

@include breakpoint-between($md, $xl) {
  .desktop-move-notifications .popup__wrapper {
    left: unset;
  }
}

#category-tooltip {
  &.tool-tip__wrapper {
    background-color: $color__white;
    justify-content: start;
    width: auto;

    .k-treeview {
      overflow: hidden;
    }

    &.bottom {
      top: calc(100%);
    }

    .k-focus {
      box-shadow: none;
    }

    .k-treeview-leaf:hover {
      cursor: pointer;
    }
  }

  li[aria-selected='true'] {
    .k-treeview-toggle {
      background-color: $color__primary;

      .k-icon::before {
        background-color: $color__primary;
        color: $color__white;
      }
    }
  }

  .k-treeview-item {
    padding-left: 0;

    > div {
      display: flex;
      flex-direction: row-reverse;
      justify-content: flex-end;
    }

    .k-treeview-leaf {
      width: 100%;
      padding-right: 2.5rem;
    }

    .k-treeview-toggle {
      margin-left: -1.5rem;
      position: relative;
      min-height: 1.75rem;
      min-width: 1.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .k-icon {
      position: relative;
      right: 0;
      z-index: 201;
      min-height: 1.5rem;

      &::before {
        color: $color__primary;
        min-width: 1.5rem;
        position: relative;
        left: .2rem;
      }
    }

    .k-i-caret-alt-right {
      &::before {
        content: '\e014';
      }
    }

    .k-i-caret-alt-down {
      &::before {
        content: '\e015';
      }
    }
  }

  .k-treeview ul li ul li {
    .k-treeview-leaf {
      padding-left: 1.5rem;
    }
  }
}

.nav .nav__main__service_menu {
  a {
    font-weight: $base-text-font-weight-semi-bold;
    text-decoration: none;
  }

  a:hover {
    color: $color__primary;
  }
}

.logged-out .nav__main__container.nav__main__logos {
  margin-right: 0;
}
</style>
